import {
  INPROGRESS_REQUESTS,
  MARKETPLACE_INSTALL_REQUEST_TYPES,
  PRODUCT_NAME_CONSTANTS
} from 'vrlcm-gui-constants';
import {
  AbstractApiResponse,
  AppSizeCheck,
  EulaForSlugResponse,
  InprogressSlugData,
  LCMMarketplaceFilterListAPIResponse,
  LCMMarketplaceItem,
  MarketplaceFilterApiResponse,
  MarketplaceFilterListAPIResponse,
  MarketplaceInstallProperties,
  MarketplaceItem,
  MarketplaceQueryOptions,
  MarketplaceTaskResponse,
  RequestId,
  RequestName,
  RequestState,
  VraTenantAndLicenseRequest,
  VraTenantAndLicenseResponse,
  MarketplaceSyncStatus,
  MPUserInformation
} from 'vrlcm-gui-types';
import {
  extractMarketplaceErrors,
  extractMarketplaceSuccessData,
  filterEnvironmentSelectionMetadataBasedOnContent,
  Http,
  isMarketplaceJobSuccessful,
  mapMarketplaceFilterAPIResponse,
  safeJsonParse
} from 'vrlcm-gui-utils';
import { DatacenterApi, EnvironmentApi, SettingsApi } from '../lcops';
import { RequestApi } from '../request';
import { MPRequestApi } from '../request/MPRequest';
import { MarketplaceApiUri } from './uri';

interface MarketplaceCache {
  marketplaceFilters?: MarketplaceFilterApiResponse;
}

type MarketplaceCachedItem = keyof MarketplaceCache;

export class MarketplaceApi {
  private static marketplaceCache: MarketplaceCache = {};

  public static async getMarketplaceItems(
    options: MarketplaceQueryOptions
  ): Promise<MarketplaceFilterListAPIResponse> {
    const defaultProdFilters = [
      'vRealize Log Insight',
      'vRealize Orchestrator',
      'vRealize Operations',
      'vRealize Automation'
    ];
    const payload = {
      products:
        options.products && options?.products?.length > 0
          ? options.products.map(
              prod =>
                PRODUCT_NAME_CONSTANTS[
                  prod as keyof typeof PRODUCT_NAME_CONSTANTS
                ]
            )
          : defaultProdFilters,
      categories: options.categories || [],
      publishers: options.publishers || [],
      page: options.currentPage,
      pageSize: options.pageSize,
      search: options.search || ''
    };
    const response = await Http.post(
      MarketplaceApiUri.GET_CLOUD_MARKETPLACE_CONTENTS_URL,
      payload
    );
    return response.data.response;
  }

  public static async getLCMMarketplaceItems(
    options: MarketplaceQueryOptions
  ): Promise<LCMMarketplaceFilterListAPIResponse> {
    const response = await Http.get(
      MarketplaceApiUri.GET_LCM_MARKETPLACE_ITEMS_URL(options)
    );
    return response.data;
  }

  public static async getMarketplaceItemBySlug(
    slug: string
  ): Promise<MarketplaceItem> {
    const response = await Http.get(
      MarketplaceApiUri.MARKETPLACE_ITEM_BY_SLUG_URL(slug)
    );
    return response.data.response.data;
  }

  public static async triggerMarketPlaceEULARequest(contentId: string) {
    const response = await Http.get(
      MarketplaceApiUri.TRIGGER_MARKETPLACE_EULA(contentId),
      {
        responseType: 'arraybuffer'
      }
    );

    const contentType =
      response.headers['content-type'] === 'application/pdf' ? 'pdf' : 'json';

    return { data: response.data, contentType };
  }

  public static async getLCMMarketplaceItemBySlug(
    slug: string,
    version: string
  ): Promise<LCMMarketplaceItem> {
    const response = await Http.get(
      MarketplaceApiUri.LCM_MARKETPLACE_ITEM_BY_SLUG_URL(slug, version)
    );
    return response.data;
  }

  public static async getMarketplaceFilters() {
    const { marketplaceFilters } = MarketplaceApi.marketplaceCache;

    if (marketplaceFilters) {
      return marketplaceFilters;
    }

    const response = await Http.get<MarketplaceFilterApiResponse>(
      MarketplaceApiUri.GET_MARKETPLACE_FILTER_CATAGORIES_URL
    );
    MarketplaceApi.marketplaceCache.marketplaceFilters = response.data;
    return response.data;
  }

  public static async getMarketplaceFiltersMetadata() {
    const response = await MarketplaceApi.getMarketplaceFilters();
    return mapMarketplaceFilterAPIResponse(response);
  }

  public static async getLatestMarketplaceSyncStatus(
    postInitFunction?: (cancelFunction: () => void) => void
  ) {
    const response = await RequestApi.getRequestWithQueryOptions(
      {
        filter: {
          requestTypes: [RequestName.MarketplaceAppsSync]
        },
        sortByDirection: 'DESC',
        sortBy: 'lastUpdatedOn',
        limit: 1
      },
      postInitFunction
    );
    return response.length > 0 ? response[0] : undefined;
  }

  public static async getMarketplaceSyncDoneAtleastOnce(
    postInitFunction?: (cancelFunction: () => void) => void
  ) {
    const response = await RequestApi.getRequestWithQueryOptions(
      {
        filter: {
          requestTypes: [RequestName.MarketplaceAppsSync],
          requestStates: [RequestState.COMPLETED]
        },
        limit: 1
      },
      postInitFunction
    );
    return response.length > 0;
  }

  public static async triggerMarketplaceSync() {
    await Http.post(MarketplaceApiUri.TRIGGER_MARKETPLACE_SYNC_URL);
  }

  public static async triggerMarketplaceAppDownload(
    contentId: string,
    slug: string,
    version: string,
    userInformation: MPUserInformation
  ): Promise<RequestId> {
    const response = await Http.post(
      MarketplaceApiUri.TRIGGER_MARKETPLACE_APP_DOWNLOAD_URL,
      {
        contentId,
        slug,
        contentVersion: version,
        userInformation
      }
    );
    return {
      requestId: response.data.vmid
    };
  }

  public static async triggerMarketplaceAppInstall(data: {
    environmentId: string;
    productType: string;
    properties: MarketplaceInstallProperties;
    slug: string;
    contentVersion: string;
  }): Promise<RequestId> {
    const response = await Http.post(
      MarketplaceApiUri.TRIGGER_MARKETPLACE_APP_INSTALL_URL,
      {
        ...data,
        productType: data.productType === 'vro' ? 'vra' : data.productType
      }
    );
    return {
      requestId: response.data.vmid
    };
  }

  public static async getInprogressContentSlugs(
    postInitFunction?: (cancelFunction: () => void) => void
  ): Promise<InprogressSlugData[]> {
    const response = await RequestApi.getRequestWithQueryOptions(
      {
        filter: {
          requestStates: INPROGRESS_REQUESTS,
          requestTypes: [
            RequestName.MarketplaceAppsDownload,
            ...MARKETPLACE_INSTALL_REQUEST_TYPES
          ]
        }
      },
      postInitFunction
    );
    return response.map(r => ({
      requestId: r.vmid,
      slug: r.inputMap.slug,
      requestType: r.inputMap.downloadPartOfUpgrade
        ? RequestName.MarketplaceAppsUpgrade
        : r.requestName
    }));
  }

  public static async getDCAndEnvSelectionData(item: LCMMarketplaceItem) {
    const [datacenter, environments] = await Promise.all([
      DatacenterApi.getDatacenters(),
      EnvironmentApi.getAllEnvironmentsWithState(RequestState.COMPLETED)
    ]);
    return filterEnvironmentSelectionMetadataBasedOnContent(
      datacenter,
      environments,
      item
    );
  }

  public static async deleteMarketplaceContent(
    slug: string,
    contentId: string,
    cloudMarketplaceContent: boolean,
    contentVersion: string,
    contentFile: string
  ) {
    await Http.post(MarketplaceApiUri.MARKETPLACE_ITEM_DELETE, {
      slug,
      contentId,
      cloudMarketplaceContent,
      contentVersion,
      contentFile
    });
  }

  public static async getVraLicenseAndTenantData(
    data: {
      datacenter: string;
      environmentId: string;
      productVersion: string;
      properties: {};
    },
    postInitFunction?: (cancelFunction: () => void) => void
  ): Promise<AbstractApiResponse<VraTenantAndLicenseResponse[]>> {
    return MPRequestApi.triggerJobAndReturnResponse<
      VraTenantAndLicenseRequest,
      MarketplaceTaskResponse<string>
    >(
      MarketplaceApiUri.GET_MARKETPLACE_VRA_TENANT_LICENSE_INFO_URL,
      {
        ...data,
        productType: 'vra',
        slug: ''
      },
      postInitFunction
    ).then(res => ({
      success: isMarketplaceJobSuccessful(res.data),
      data: safeJsonParse(
        extractMarketplaceSuccessData(res.data) as string
      ) as VraTenantAndLicenseResponse[],
      errors: extractMarketplaceErrors(res.data),
      when: new Date()
    }));
  }

  public static async getEulaDataForSlug(
    slug: string,
    postInitFunction?: (cancelFunction: () => void) => void
  ): Promise<AbstractApiResponse<EulaForSlugResponse>> {
    return MPRequestApi.triggerJobAndReturnResponse<
      void,
      MarketplaceTaskResponse<EulaForSlugResponse>
    >(
      MarketplaceApiUri.GET_MARKETPLACE_EULA_FOR_SLUG_URL(slug),
      undefined,
      postInitFunction,
      'GET'
    ).then(res => ({
      success: isMarketplaceJobSuccessful(res.data),
      data: safeJsonParse(
        extractMarketplaceSuccessData(res.data) as string
      ) as EulaForSlugResponse,
      errors: extractMarketplaceErrors(res.data),
      when: new Date()
    }));
  }

  public static async updateEulaDataForSlug(
    slug: string,
    postInitFunction?: (cancelFunction: () => void) => void
  ): Promise<AbstractApiResponse<EulaForSlugResponse>> {
    return MPRequestApi.triggerJobAndReturnResponse<
      void,
      MarketplaceTaskResponse<EulaForSlugResponse>
    >(
      MarketplaceApiUri.GET_MARKETPLACE_EULA_FOR_SLUG_URL(slug),
      undefined,
      postInitFunction
    ).then(res => ({
      success: isMarketplaceJobSuccessful(res.data),
      data: safeJsonParse(
        extractMarketplaceSuccessData(res.data) as string
      ) as EulaForSlugResponse,
      errors: extractMarketplaceErrors(res.data),
      when: new Date()
    }));
  }

  public static async getMarketplaceInitialSyncStatus(
    checkForMyVMwareSettings?: boolean,
    postInitFunction?: (cancelFunction: () => void) => void
  ): Promise<MarketplaceSyncStatus> {
    const cancelFunctions: Array<() => void> = [];
    if (postInitFunction) {
      const cancel = () => cancelFunctions.forEach(cf => cf());
      postInitFunction(cancel);
    }

    if (checkForMyVMwareSettings) {
      const response = await Promise.all([
        SettingsApi.getMyVMwareAccountSettings(),
        MarketplaceApi.getLatestMarketplaceSyncStatus(cf =>
          cancelFunctions.push(cf)
        ),
        MarketplaceApi.getMarketplaceSyncDoneAtleastOnce(cf =>
          cancelFunctions.push(cf)
        )
      ]);
      return {
        atleastOnceSyncDone: !!response[2],
        myvmwareConfigured: !!response[0],
        latestSyncStatus: response[1]
          ? {
              state: response[1].state,
              requestId: response[1].vmid
            }
          : undefined
      };
    } else {
      const response = await Promise.all([
        MarketplaceApi.getLatestMarketplaceSyncStatus(cf =>
          cancelFunctions.push(cf)
        ),
        MarketplaceApi.getMarketplaceSyncDoneAtleastOnce(cf =>
          cancelFunctions.push(cf)
        )
      ]);
      return {
        atleastOnceSyncDone: !!response[1],
        myvmwareConfigured: true,
        latestSyncStatus: response[0]
          ? {
              state: response[0].state,
              requestId: response[0].vmid
            }
          : undefined
      };
    }
  }

  public static async checkAppSize(
    fileSize: number,
    externalLink: string
  ): Promise<AppSizeCheck> {
    const response = await SettingsApi.getSystemInformation();
    return {
      sizecheckStatus: +response.availableSizeInBytes - fileSize > 0,
      hostedStatus: externalLink === 'true'
    };
  }

  public static clearMarketplaceCache() {
    MarketplaceApi.marketplaceCache = {};
  }

  public static clearMarketplaceCacheItem(item: MarketplaceCachedItem) {
    MarketplaceApi.marketplaceCache[item] = undefined;
  }
}
